<template>
  <div class="pb-20 pt-36">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Dasha - Project Manager & Business Development"
      description="Meet Dasha, our project manager and business development expert with proven track record in international business development and strategic communications."
      :keywords="['Dasha', 'project manager', 'business development', 'strategic communications', 'international business', 'project management']"
      url="https://vedware.com/about/dasha"
    />

    <div class="container mx-auto">
      <!-- Page Header -->
      <PageHeader
        title="Meet Our Project Manager"
        description="Strategic communications expert with proven track record in international business development"
      />

      <!-- Project Manager Profile -->
      <div class="mt-10 block sm:mt-20 sm:flex sm:gap-10">
        <!-- About profile image and social links -->
        <div class="mb-7 w-full sm:mb-0 sm:w-1/4">
          <img src="/images/dasha.jpg" class="w-96 rounded-lg shadow-soft" alt="Project Manager Profile">

          <!-- Social Links -->
          <SocialLinks :social-links="socialLinks" />
        </div>

        <!-- About details -->
        <div class="w-full text-left text-lg text-ternary-dark dark:text-ternary-light sm:w-3/4">
          <!-- Introduction -->
          <div class="mb-8">
            <h2 class="mb-4 text-3xl font-bold text-ternary-dark dark:text-primary-light">
              About Dasha
            </h2>
            <p class="mb-4 leading-relaxed">
              She brings over <span class="font-semibold text-accent dark:text-accent">4+ years of international business experience</span> and a strong background in <span class="font-semibold text-accent dark:text-accent">strategic communications</span>. Specializing in project management, stakeholder engagement, and cross-cultural collaboration - from initial planning to successful project delivery.
            </p>
            <p class="mb-4 leading-relaxed">
              She has successfully managed projects across various industries including <span class="font-semibold">cryptocurrency platforms</span>, <span class="font-semibold">healthcare advocacy</span>, <span class="font-semibold">technology startups</span>, and <span class="font-semibold">international partnerships</span>, often working with global teams and diverse stakeholders.
            </p>
          </div>

          <!-- Project Management Expertise -->
          <ExpertiseSection
            title="Project Management Excellence"
            emoji="📋"
            description="She specializes in <span class='font-semibold text-accent dark:text-accent'>strategic planning</span>, <span class='font-semibold text-accent dark:text-accent'>stakeholder management</span>, and <span class='font-semibold text-accent dark:text-accent'>cross-cultural communication</span>. Her goal is to ensure projects are delivered on time, within budget, and exceed expectations while maintaining strong team collaboration."
            :cards="projectManagementCards"
          />

          <!-- Communication & Leadership -->
          <ExpertiseSection
            title="Communication & Leadership"
            emoji="💬"
            description="With expertise in <span class='font-semibold text-accent dark:text-accent'>strategic communications</span> and <span class='font-semibold text-accent dark:text-accent'>public policy</span>, she excels at building relationships, managing complex stakeholder networks, and driving organizational growth through effective communication strategies."
            :cards="communicationCards"
          />

          <!-- Work Style & Strengths -->
          <InfoCards :cards="infoCards" />

          <!-- Call to Action -->
          <div class="mt-8 rounded-xl bg-gradient-to-r from-accent/10 to-gradient-end/10 p-6 text-center dark:from-accent/20 dark:to-gradient-end/20">
            <p class="mb-4 text-lg font-medium text-ternary-dark dark:text-primary-light">
              Looking for a project manager who can drive your initiatives forward with strategic planning and effective communication?
            </p>
            <p class="font-semibold text-accent dark:text-accent">
              Let's discuss your next project.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics -->
    <AboutCounter />

    <!-- Brands Section -->
    <AboutClients />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import { PhEnvelope, PhGlobe } from '@phosphor-icons/vue'
import AboutClients from '~/components/About/AboutClients.vue'
import AboutCounter from '~/components/About/AboutCounter.vue'
import ExpertiseSection from '~/components/About/ExpertiseSection.vue'
import InfoCards from '~/components/About/InfoCards.vue'
import PageHeader from '~/components/About/PageHeader.vue'
import SocialLinks from '~/components/About/SocialLinks.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const socialLinks = ref([
  // {
  //   name: 'LinkedIn',
  //   url: 'https://www.linkedin.com/in/daria-mironova-908962b4/',
  //   icon: PhLinkedinLogo,
  //   bgColor: 'bg-blue-600',
  //   subtitle: 'Professional Profile',
  // },
  {
    name: 'Email',
    url: 'mailto:dasha@vedware.com',
    icon: PhEnvelope,
    bgColor: 'bg-accent',
    subtitle: 'dasha@vedware.com',
  },
  {
    name: 'Portfolio',
    url: '#',
    icon: PhGlobe,
    bgColor: 'bg-gradient-to-r from-accent to-gradient-end',
    subtitle: 'Coming Soon',
  },
])

const projectManagementCards = ref([
  {
    title: 'Core Skills',
    emoji: '🛠',
    content: 'Strategic Planning, Stakeholder Management, Risk Assessment',
  },
  {
    title: 'Methodologies',
    emoji: '🔄',
    content: 'Agile, Scrum, Waterfall, Hybrid approaches',
  },
  {
    title: 'Tools & Platforms',
    emoji: '💻',
    content: 'Jira, Asana, Trello, Slack, Microsoft Project',
  },
  {
    title: 'Industry Experience',
    emoji: '🌐',
    content: 'Crypto, Healthcare, Technology, International Business',
  },
])

const communicationCards = ref([
  {
    title: 'Strategic Communications',
    emoji: '📢',
    content: 'Public policy advocacy, stakeholder engagement, brand positioning',
  },
  {
    title: 'Cross-Cultural Leadership',
    emoji: '🤝',
    content: 'International team management, cultural sensitivity, global partnerships',
  },
  {
    title: 'Public Relations',
    emoji: '📊',
    content: 'Media relations, crisis communication, organizational messaging',
  },
  {
    title: 'Partnership Development',
    emoji: '🌍',
    content: 'Strategic alliances, business development, relationship building',
  },
])

const infoCards = ref([
  {
    title: 'Work Style',
    emoji: '💼',
    items: [
      'Remote and international teams',
      'Collaborate with diverse stakeholders',
      'Clear communication via modern tools',
      'Long-term strategic focus',
    ],
  },
  {
    title: 'Key Strengths',
    emoji: '💡',
    items: [
      'Strategic project planning',
      'Cross-cultural communication',
      'Stakeholder relationship management',
      'International business development',
    ],
  },
])
</script>
